// @ts-check
import { state } from '../state';
import { placeBet } from './place';
import { removeItem } from './items';
import { getItemHashKey, saveState } from './helpers';
import { addUserBetslip, setToastNotification, userBetslipsStore } from '@bto-services/toast-notification';
import { getToastAdditionalTime, TOAST_NOTIFICATION_MESSAGE_STATUS } from 'bto-models/toast-notification';
import { userLoggedIn } from '@bto-services/user';
import { clearBetslip, freebetStore, rebet } from '..';

/**
 * Quickbet key store, Map of betIdentifier -> item key
 * Used to quickly get item key during signalr place notification messages
 * @type {{[betIdentifier:string]:string}}
 */
export const quickBetKeyStore = {};

/**
 * Quickly place a single bet if quickbet is active and properly configured
 * @param {import('bto-models/betslip/interfaces').BetslipItem} item - betslip item
 */
export function placeQuickBet(item) {
  if (!state.quickBetActive) {
    return false;
  }
  // If stake is not present show notify user and remove item
  if (!state.quickBetStake && state.freebetId === 0) {
    const expireTime = Date.now() + getToastAdditionalTime();
    const key = `no_quickbet_stake`;
    setToastNotification(key, {
      playerId: APP_SETTINGS.player.id,
      expireTime,
      message: 'Mobile.PleaseSetQBStake',
      status: TOAST_NOTIFICATION_MESSAGE_STATUS.ERROR,
    });
    removeItem(item);
    return false;
  }
  /**
   * If user is not logged and external login metod is not set display notification and remove item
   * If user is not logged but the login popup is set then it will be called during place, and the item
   * will be removed at the end of place quickbet
   */
  if (!userLoggedIn() && !APP_SETTINGS.settings.externalLoginMethod) {
    const expireTime = Date.now() + getToastAdditionalTime();
    const key = `quickbet_no_user`;
    setToastNotification(key, {
      playerId: APP_SETTINGS.player.id,
      expireTime,
      message: 'Mobile.LoginFirst',
      status: TOAST_NOTIFICATION_MESSAGE_STATUS.ERROR,
    });
    removeItem(item);
    return false;
  }
  const itemKey = getItemHashKey(item);
  /**
   * We can quickly set the signle stake and calculate the totals
   * since we are placing single bet so no need to calculate bonuses to get totals
   */
  state.singles[itemKey] = state.quickBetStake;
  state.totals = {
    // stake: +state.quickBetStake,
    stake: checkQuickBetStakeSource(),
    maxOdds: item.odd,
    minOdds: item.odd,
  };
  placeBet();
  removeItem(item);
  return true;
}

/**
 * Add quick bet mapping for place identifer -> item key
 * @param {string} key - bet place unique identifier
 * @param {string} msg - item key
 */
export function addQuickBetMessage(key, msg) {
  quickBetKeyStore[key] = msg;
}

/**
 * Remove quick bet key from store
 * @param {string} key - place identifier
 */
export function removeQuickBetMessage(key) {
  delete quickBetKeyStore[key];
}

/**
 * Toggle quickbet functionalliry
 */
export function toggleQuickBet() {
  const quickBetActive = !state.quickBetActive;
  const acceptOddChange = state.acceptOddChange;
  // On quickbet toggle if quickbet is activated save current betslip state
  if (quickBetActive) {
    addUserBetslip('quickbet', state);
    clearBetslip();
    state.acceptOddChange = acceptOddChange;
  }
  // On quickbet toggle if quickbet is deactivated return to previous state
  if (!quickBetActive && userBetslipsStore['quickbet']) {
    rebet(userBetslipsStore['quickbet']);
  }
  state.quickBetActive = quickBetActive;
  saveState();
}

/**
 * Reset betslip to regular mode if quickbet is active
 * This is called when we try to add unsuported quickbet item into betslip
 */
export function resetQuickbet() {
  if (state.quickBetActive) {
    toggleQuickBet();
  }
}

/**
 * This is called to check if QuickBet is using FreeBet,
 * or the stake is set through QuickBet's stakes/free amount
 */
function checkQuickBetStakeSource() {
  if (state.quickBetActive && state.quickBetStake) {
    return +state.quickBetStake;
  }
  if (state.quickBetActive && !state.quickBetStake && state.freebetId !== 0) {
    return freebetStore.freebets.find(x => x.UserFreeBetID === state.freebetId).Amount;
  }
  return 0;
}